import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCog, 
  faEdit, 
  faTrash, 
  faToggleOn, 
  faToggleOff, 
  faPlus,
  faSave,
  faTimes,
  faRefresh,
  faShippingFast,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import './Parameters.css';

const Parameters = () => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingParameter, setEditingParameter] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    type: 'string',
    description: ''
  });
  const [showShippingCalculator, setShowShippingCalculator] = useState(false);
  const [shippingTestData, setShippingTestData] = useState({
    cartTotal: 0
  });
  const [shippingResult, setShippingResult] = useState(null);
  const [maintenanceModeValue, setMaintenanceModeValue] = useState(null);
  const [maintenanceMessageValue, setMaintenanceMessageValue] = useState('');
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  useEffect(() => {
    loadParameters();
  }, []);

  const loadParameters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/parameters');
      if (response.data.success) {
        const allParams = response.data.data;
        setParameters(allParams);

        const maintenanceModeParam = allParams.find(p => p.param_key === 'maintenance_mode');
        const maintenanceMessageParam = allParams.find(p => p.param_key === 'maintenance_message');

        if (maintenanceModeParam) {
          setMaintenanceModeValue(maintenanceModeParam.param_value === 'true');
        }

        if (maintenanceMessageParam) {
          setMaintenanceMessageValue(maintenanceMessageParam.param_value || '');
        }
      }
    } catch (error) {
      console.error('Parametreler yüklenirken hata:', error);
      toast.error('Parametreler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (parameter) => {
    setEditingParameter(parameter);
    setFormData({
      key: parameter.param_key,
      value: parameter.param_value,
      type: parameter.param_type,
      description: parameter.description || ''
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingParameter(null);
    setFormData({
      key: '',
      value: '',
      type: 'string',
      description: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.key || formData.value === '') {
        toast.error('Parametre anahtarı ve değeri gerekli');
        return;
      }

      const response = await api.post('/admin/parameters', formData);
      if (response.data.success) {
        toast.success('Parametre başarıyla kaydedildi');
        setShowModal(false);
        loadParameters();
      }
    } catch (error) {
      console.error('Parametre kaydedilirken hata:', error);
      toast.error('Parametre kaydedilirken hata oluştu');
    }
  };

  const handleToggle = async (key, currentStatus) => {
    try {
      const response = await api.patch(`/admin/parameters/${key}/toggle`, {
        isActive: !currentStatus
      });
      if (response.data.success) {
        toast.success(`Parametre ${!currentStatus ? 'aktif' : 'pasif'} hale getirildi`);
        loadParameters();
      }
    } catch (error) {
      console.error('Parametre durumu değiştirilirken hata:', error);
      toast.error('Parametre durumu değiştirilirken hata oluştu');
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Bu parametreyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await api.delete(`/admin/parameters/${key}`);
      if (response.data.success) {
        toast.success('Parametre başarıyla silindi');
        loadParameters();
      }
    } catch (error) {
      console.error('Parametre silinirken hata:', error);
      toast.error('Parametre silinirken hata oluştu');
    }
  };

  const clearCache = async () => {
    try {
      const response = await api.post('/admin/parameters/clear-cache');
      if (response.data.success) {
        toast.success('Önbellek temizlendi');
      }
    } catch (error) {
      console.error('Önbellek temizlenirken hata:', error);
      toast.error('Önbellek temizlenirken hata oluştu');
    }
  };

  const saveMaintenanceSettings = async () => {
    try {
      setSavingMaintenance(true);

      // maintenance_mode parametresi
      await api.post('/admin/parameters', {
        key: 'maintenance_mode',
        value: maintenanceModeValue ? 'true' : 'false',
        type: 'boolean',
        description: 'Bakım modu aktif/pasif'
      });

      // maintenance_message parametresi
      await api.post('/admin/parameters', {
        key: 'maintenance_message',
        value: maintenanceMessageValue || 'Sistem bakımda, lütfen daha sonra tekrar deneyin.',
        type: 'string',
        description: 'Bakım modu mesajı'
      });

      toast.success('Bakım modu ayarları güncellendi');
      loadParameters();
    } catch (error) {
      console.error('Bakım modu ayarları kaydedilirken hata:', error);
      toast.error('Bakım modu ayarları kaydedilirken hata oluştu');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const testShippingCalculation = async () => {
    try {
      const response = await api.post('/admin/parameters/calculate-shipping', {
        cartTotal: parseFloat(shippingTestData.cartTotal)
      });
      if (response.data.success) {
        setShippingResult(response.data.data);
      }
    } catch (error) {
      console.error('Kargo hesaplama testi hatası:', error);
      toast.error('Kargo hesaplama testi başarısız');
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      string: 'primary',
      number: 'success',
      boolean: 'warning',
      json: 'info'
    };
    return <Badge bg={badges[type] || 'secondary'}>{type}</Badge>;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Aktif</Badge>
    ) : (
      <Badge bg="danger">Pasif</Badge>
    );
  };

  const formatValue = (value, type) => {
    if (type === 'boolean') {
      return value === 'true' ? 'Evet' : 'Hayır';
    }
    if (type === 'json') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faCog} className="me-2" />
                Sistem Parametreleri
              </h5>
              <div>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={clearCache}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faRefresh} className="me-2" />
                  Önbelleği Temizle
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  onClick={() => setShowShippingCalculator(true)}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faCalculator} className="me-2" />
                  Kargo Testi
                </Button>
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={handleCreate}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Yeni Parametre
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={12}>
                  <Card className="mb-3">
                    <Card.Header>
                      <strong>Bakım Modu</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row className="align-items-center mb-3">
                        <Col md={6}>
                          <Form.Check 
                            type="switch"
                            id="maintenance-mode-switch"
                            label="Bakım modunu aktifleştir"
                            checked={!!maintenanceModeValue}
                            onChange={(e) => setMaintenanceModeValue(e.target.checked)}
                          />
                        </Col>
                        <Col md={6} className="text-md-end mt-2 mt-md-0">
                          {maintenanceModeValue ? (
                            <Badge bg="danger">Bakım modu AKTİF</Badge>
                          ) : (
                            <Badge bg="success">Bakım modu PASİF</Badge>
                          )}
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Bakım Mesajı</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={maintenanceMessageValue}
                          onChange={(e) => setMaintenanceMessageValue(e.target.value)}
                          placeholder="Sistem bakımda, lütfen daha sonra tekrar deneyin."
                        />
                        <Form.Text muted>
                          Bu mesaj bakım modu aktifken müşterilere gösterilecektir. Admin paneline erişim devam eder.
                        </Form.Text>
                      </Form.Group>
                      <div className="text-end">
                        <Button 
                          variant="primary" 
                          onClick={saveMaintenanceSettings}
                          disabled={savingMaintenance}
                        >
                          {savingMaintenance && (
                            <Spinner 
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                          )}
                          Bakım Modu Ayarlarını Kaydet
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Anahtar</th>
                    <th>Değer</th>
                    <th>Tip</th>
                    <th>Açıklama</th>
                    <th>Durum</th>
                    <th>Güncelleme</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((param) => (
                    <tr key={param.id}>
                      <td>
                        <strong>{param.param_key}</strong>
                      </td>
                      <td>
                        <div className="parameter-value">
                          {formatValue(param.param_value, param.param_type)}
                        </div>
                      </td>
                      <td>{getTypeBadge(param.param_type)}</td>
                      <td>{param.description || '-'}</td>
                      <td>{getStatusBadge(param.is_active)}</td>
                      <td>
                        <small className="text-muted">
                          {new Date(param.updated_at).toLocaleString('tr-TR')}
                        </small>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(param)}
                          className="me-1"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant={param.is_active ? "outline-warning" : "outline-success"}
                          size="sm"
                          onClick={() => handleToggle(param.param_key, param.is_active)}
                          className="me-1"
                        >
                          <FontAwesomeIcon icon={param.is_active ? faToggleOff : faToggleOn} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(param.param_key)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Parametre Düzenleme Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingParameter ? 'Parametre Düzenle' : 'Yeni Parametre'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parametre Anahtarı</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({...formData, key: e.target.value})}
                    disabled={!!editingParameter}
                    placeholder="örn: shipping_fee"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tip</Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="json">JSON</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Değer</Form.Label>
              {formData.type === 'boolean' ? (
                <Form.Select
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                >
                  <option value="true">Evet (true)</option>
                  <option value="false">Hayır (false)</option>
                </Form.Select>
              ) : formData.type === 'json' ? (
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  placeholder='{"key": "value"}'
                />
              ) : (
                <Form.Control
                  type={formData.type === 'number' ? 'number' : 'text'}
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  placeholder="Değer girin"
                />
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Parametre açıklaması"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            İptal
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Kargo Hesaplama Test Modal */}
      <Modal show={showShippingCalculator} onHide={() => setShowShippingCalculator(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faShippingFast} className="me-2" />
            Kargo Ücreti Hesaplama Testi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Sepet Tutarı (TL)</Form.Label>
            <Form.Control
              type="number"
              value={shippingTestData.cartTotal}
              onChange={(e) => setShippingTestData({...shippingTestData, cartTotal: e.target.value})}
              placeholder="0"
            />
          </Form.Group>
          
          <Button 
            variant="primary" 
            onClick={testShippingCalculation}
            className="w-100"
          >
            <FontAwesomeIcon icon={faCalculator} className="me-2" />
            Hesapla
          </Button>

          {shippingResult && (
            <Alert variant="info" className="mt-3">
              <h6>Hesaplama Sonucu:</h6>
              <div><strong>Sepet Tutarı:</strong> {shippingResult.cartTotal} TL</div>
              <div><strong>Kargo Ücreti:</strong> {shippingResult.shippingFee} TL</div>
              <div><strong>Ücretsiz Kargo Limiti:</strong> {shippingResult.freeShippingThreshold} TL</div>
              <div><strong>Kargo Aktif:</strong> {shippingResult.shippingEnabled ? 'Evet' : 'Hayır'}</div>
              <div><strong>Ücretsiz Kargo:</strong> {shippingResult.isFreeShipping ? 'Evet' : 'Hayır'}</div>
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Parameters;
